import React, { useCallback, useMemo, useState } from 'react';
import { PAGE_CONFIG } from './Config';
import EmptyPage from '../../components/EmptyPage';
import { useTranslation } from 'react-i18next';
import PageIntro from '../../components/PageIntro';
import ContentCard from '../../components/ContentCard';
import TablePagination from '../../components/Pagination';
import tableStyles from '../../styles/tableStyles';
import api from '../../api';
import { useQuery } from '@tanstack/react-query';

import { isDedicatedCatalogType } from '../../catalog/catalogGroups';

/** Material types already covered by their own top-level catalog pages. */
function isDedicatedCatalogMaterialType(name) {
    return isDedicatedCatalogType(name);
}

const Other = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`other:${key}`), [t]);
    const pageConfig = useMemo(() => PAGE_CONFIG(tr), [tr]);

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });

    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    const { data: materialTypes } = useQuery({
        queryKey: ['materialTypes'],
        queryFn: () => api.materialTypes.getMaterialTypes(),
        select: (data) => data?.data?.data
    });

    const otherTypeIds = useMemo(() => {
        if (!materialTypes?.length) return [];
        return materialTypes.filter((mt) => mt?.name && !isDedicatedCatalogMaterialType(mt.name)).map((mt) => mt.id);
    }, [materialTypes]);

    const queryParams = useMemo(
        () => ({
            ...params,
            materialTypeId: otherTypeIds.length ? otherTypeIds.join(',') : null
        }),
        [params, otherTypeIds]
    );

    const { data: uploadsResponse, isLoading } = useQuery({
        queryKey: ['other', queryParams],
        queryFn: () => api.upload.getUploads(queryParams),
        enabled: otherTypeIds.length > 0,
        select: (data) => data?.data
    });

    const items = uploadsResponse?.data;
    const pagination = {
        page: params.page,
        limit: params.limit,
        totalCount: uploadsResponse?.totalCount || 0
    };
    const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

    return (
        <div>
            <PageIntro description={pageConfig.PAGE_DESCRIPTION} />

            {otherTypeIds.length > 0 ? (
                <>
                    <div className="text-sm text-gray-500 ml-2 mt-4 dark:text-gray-400">
                        {tr('showing')} {items?.length ?? 0} {tr('of')} {pagination.totalCount}
                    </div>

                    {items && items.length > 0 ? (
                        <ContentCard data={items} />
                    ) : (
                        <EmptyPage
                            colSpan={12}
                            isLoading={isLoading}
                            icon={pageConfig.EMPTY_PAGE_ICON}
                            description={pageConfig.SEARCH_DESCRIPTION}
                            placeholder={pageConfig.SEARCH_PLACEHOLDER}
                            loadingMessage={pageConfig.SEARCH_LOADING_MESSAGE}
                        />
                    )}

                    {items && items.length > 0 && (
                        <TablePagination
                            totalPages={totalPages}
                            page={pagination.page}
                            limit={pagination.limit}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            tableStyles={tableStyles}
                        />
                    )}
                </>
            ) : (
                <div className="mt-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center text-gray-600 dark:text-gray-300">
                    {tr('noCategories')}
                </div>
            )}
        </div>
    );
};

export default Other;
